import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './LoginPage.css';

// Login Page Component - SSO entry point for Antasis Ecosystem
// Displays login form with email/password fields
// Includes discreet mock login buttons for development

interface LoginPageProps {
  onLoginSuccess: (email: string, role: string) => void;
}

interface UserRole {
  id: string;
  label: string;
  email: string;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Mock user roles for quick login
  const mockRoles: UserRole[] = [
    { id: 'agent', label: 'Agent', email: 'agent@antasis.local' },
    { id: 'support', label: 'Support', email: 'support@antasis.local' },
    { id: 'management', label: 'Management', email: 'management@antasis.local' }
  ];

  // Main login handler
  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      if (email && password) {
        onLoginSuccess(email, 'user');
      }
      setIsLoading(false);
    }, 600);
  };

  // Quick mock login for development
  const handleMockLogin = (role: UserRole) => {
    setIsLoading(true);
    setTimeout(() => {
      onLoginSuccess(role.email, role.id);
      setIsLoading(false);
    }, 600);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const formVariants = {
    hidden: { x: 40, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.6, delay: 0.2, ease: 'easeOut' }
    }
  };

  const brandVariants = {
    hidden: { x: -40, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.6, delay: 0.2, ease: 'easeOut' }
    }
  };

  return (
    <motion.div
      className="login-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Brand Section */}
      <motion.div className="login-brand" variants={brandVariants}>
        <div className="login-brand-icon">⚙</div>
        <div className="login-brand-content">
          <h1 className="login-brand-title">Antasis Ecosystem</h1>
          <p className="login-brand-subtitle">Identity & Access Management</p>
        </div>
        <p className="login-brand-description">
          Unified Single Sign-On portal for all microservices and enterprise applications
        </p>
      </motion.div>

      {/* Login Form Section */}
      <motion.div className="login-form-section" variants={formVariants}>
        <div className="login-form-wrapper">
          <h2 className="login-title">Sign In</h2>
          <p className="login-subtitle">Enter your credentials to access Antasis Ecosystem</p>

          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                className="form-input"
                placeholder="your.email@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <input
                id="password"
                type="password"
                className="form-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Discreet Mock Login Section */}
          <div className="mock-login-section">
            <div className="mock-login-divider">Development</div>
            <div className="mock-login-buttons">
              {mockRoles.map((role) => (
                <button
                  key={role.id}
                  className="mock-login-btn"
                  onClick={() => handleMockLogin(role)}
                  disabled={isLoading}
                  title={role.email}
                >
                  {role.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default LoginPage;
